/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, SelectChangeEvent, Stack, useMediaQuery } from '@mui/material';
import FirstStep from './firstStep/FirstStep';
import SecondStep from './secondStep/SecondStep';
import ThirdStep from './ThirdStep';
import {collection, DocumentData, getDocs, query, where} from 'firebase/firestore';
import ForthStep from './ForthStep';
import { clientInfo, docInfo, formValues, orderInfo } from '../../utils/types';
import formConfig from './formConfig';
import ValidationScreen from './ValidationScreen';
import {
  navigateWithQuery,
  redirectToStripePayment,
  storeFormData,
} from '../../utils/utilFuntions';
import { useCallback, useEffect, useRef, useState } from 'react';

import { sendLogToSlack } from '../../utils/notificationFunctions';
import { logEvent } from 'firebase/analytics';
import { analytics, db } from '../../firebase-setup/firebase';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';

async function storeFYMSession(
  sessionId: string,
  formValues: formValues,
  uniqueId: string
) {
  const docref = doc(collection(db, 'fymSessions'), sessionId);
  await setDoc(docref, {
    values: formValues,
    uniqueId: uniqueId,
  });
}
function transformIdFYM(input: string) {
  const [prefix, suffix] = input.split('.');
  const lastTwoDigits = prefix.slice(-2);
  return `${lastTwoDigits}${suffix}`;
}

async function isUniqueIdStored(uniqueId: string): Promise<boolean> {
    const ref = collection(db, 'preStripeClients');
    const snapshot = await getDocs(query(ref, where('uniqueId', '==', uniqueId)));
    return !snapshot.empty;
}


async function generateUniqueId() {
    let uniqueId = Date.now() + Math.random().toString(36);
    const ref = collection(db, 'preStripeClients');
    let exists = true;

    while (exists) {
        const snapshot = await getDocs(query(ref, where('uniqueId', '==', uniqueId)));
        if (snapshot.empty) {
            exists = false;
        } else {
            // Regenerate until it's truly unique
            uniqueId = Date.now() + Math.random().toString(36);
        }
    }

    return uniqueId;
}

const DynamicStep = ({
  values,
  setValues,
  activeStep,
  setActiveStep,
  medicationsList,
  insuranceDataList,
  isMedicareOnly,
}: {
  values: formValues;
  setValues: React.Dispatch<React.SetStateAction<formValues>>;
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  medicationsList: DocumentData[] | null;
  insuranceDataList: DocumentData[] | null;
  isMedicareOnly: boolean;
}) => {
  const [validStep, setValidStep] = useState(false);
  const [sendingData, setSendingData] = useState(false);
  const [isMedicare, setIsMedicare] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const uniqueId = useRef<string>('');

  sessionStorage.setItem('uniqueId', uniqueId.current);
  sessionStorage.setItem('isMedicare', isMedicare.toString());
  const fymSessionId = transformIdFYM(uniqueId.current);
  const calIDEnum = () => {
    const today = new Date();
    const day = today.getDay(); // 0 (Sunday) to 6 (Saturday)
    switch (day) {
      case 1:
        return 'two';
      case 2:
        return 'three';
      case 3:
        return 'four';
      case 4:
        return 'five';
      case 5:
        return 'six';
      default:
        return 'one';
    }
  };

  const handleSubmit = useCallback(async () => {
    if (sendingData) return;
    setSendingData(true);
    try {
      sessionStorage.setItem('formData', JSON.stringify(values));
      const filteredOrders = values.orders.filter(
        (order) => order.medicationName !== ''
      );
      const filteredValues = { ...values, orders: filteredOrders };

      const welcomCallData = {
        name: values.patient.fname + ' ' + values.patient.lname,
        email: values.patient.email,
        calID: calIDEnum(),
      };

      sessionStorage.setItem('welcomCallData', JSON.stringify(welcomCallData));

      storeFormData(filteredValues, uniqueId.current);
      sendLogToSlack(filteredValues);

      if (
        window.location.origin === 'https://fym.transparentpricerx.com' ||
        window.location.origin ===
          'https://fym-staging.transparentpricerx.com' ||
        window.location.hostname.includes('localhost')
      ) {
        navigateWithQuery(navigate, '/checkout', location);
      } else {
        redirectToStripePayment(uniqueId.current, isMedicare);
      }
    } catch (error) {
      console.error('Error during submission:', error);
    } finally {
      setSendingData(false); // ✅ Always reset flag
    }
  }, [sendingData, values, navigate, location, isMedicare]);
  const handleNext = useCallback(() => {
    window.location.origin === 'https://fym.transparentpricerx.com' &&
      storeFYMSession(fymSessionId, values, uniqueId.current);
    if (activeStep === formConfig.length) {
      logEvent(analytics, `viewPayment`);
      handleSubmit();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setValidStep(false);
    }
  }, [activeStep, fymSessionId, handleSubmit, setActiveStep, values]);

  const handleBack = useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setValidStep(true);
  }, [setActiveStep]);
  const handleChange = useCallback(
    (
      e: { target: { name: string; value: unknown } },
      object: clientInfo | docInfo | orderInfo
    ) => {
      const { name, value } = e.target;

      const boolValue = value === 'yes' ? true : value === 'no' ? false : value;
      // Update the correct field in the object
      const updatedObject = { ...object, [name]: boolValue };

      // Use a functional update to ensure the correct previous state

      setValues((prevValues) => ({
        ...prevValues,
        patient: {
          ...prevValues.patient,
          ...updatedObject,
        },
      }));
    },
    [setValues]
  );

  const handleChangeSelect = useCallback(
    (
      e: SelectChangeEvent<unknown>,
      object: clientInfo | docInfo | orderInfo
    ) => {
      const { name, value } = e.target;

      // Update the correct field in the object
      const updatedObject = { ...object, [name]: value };

      // Use a functional update to ensure the correct previous state
      setValues((prevValues) => ({
        ...prevValues,
        patient: {
          ...prevValues.patient,
          ...updatedObject,
        },
      }));
    },
    [setValues]
  );

  const isFirstStep = activeStep === 0;

  const [numDocs, setNumDocs] = useState<number>(1);
  const [numMeds, setNumMeds] = useState<number>(4);
  const matches = useMediaQuery('(max-width:991px)');
  useEffect(() => {
    values.patient.insurance.company === 'medicare' && setIsMedicare(true);
    sessionStorage.setItem('isMedicare', isMedicare.toString());
  }, [isMedicare, values.patient.insurance.company]);

  useEffect(() => {
    if (
      window.parent !== window &&
      window.location.origin === 'https://fym.transparentpricerx.com'
    ) {
      window.parent.postMessage(
        {
          type: 'SESSION_URL',
          url: `${window.location.origin}/?sessionId=${fymSessionId}`,
        },
        '*'
      );
    }
  }, [fymSessionId]);
    useEffect(() => {
        const setUniqueId = async () => {
            const fromStorage = sessionStorage.getItem('uniqueId');
            const fromURL = new URLSearchParams(window.location.search).get('sessionId');

            const candidateId = fromStorage || fromURL || '';
            if (candidateId && await isUniqueIdStored(candidateId)) {
                uniqueId.current = candidateId;
            } else {
                uniqueId.current = await generateUniqueId();
            }

            sessionStorage.setItem('uniqueId', uniqueId.current);
        };

        setUniqueId();
    }, []);

  return (
    <Stack spacing={2}>
      {activeStep === 0 && (
        <FirstStep
          isMedicare={isMedicare}
          isMedicareOnly={isMedicareOnly}
          setIsMedicare={setIsMedicare}
          values={values}
          setValues={setValues}
          setValidStep={setValidStep}
          insuranceDataList={insuranceDataList}
        />
      )}
      {activeStep === 1 && (
        <SecondStep
          medicationsList={medicationsList}
          values={values}
          setValues={setValues}
          setValidStep={setValidStep}
          numMeds={numMeds}
          setNumMeds={setNumMeds}
        />
      )}
      {activeStep === 2 && (
        <ThirdStep
          handleChange={handleChange}
          handleChangeSelect={handleChangeSelect}
          values={values}
          setValues={setValues}
          setValidStep={setValidStep}
        />
      )}
      {activeStep === 3 && (
        <ForthStep
          numDocs={numDocs}
          setNumDocs={setNumDocs}
          values={values}
          setValues={setValues}
          setValidStep={setValidStep}
        />
      )}
      {activeStep === 4 && !sendingData && (
        <ValidationScreen
          values={values}
          setValidStep={setValidStep}
          setValues={setValues}
        />
      )}
      {activeStep === 4 && sendingData && (
        <div className="loader-s">
          <div className="loader" />
        </div>
      )}

      <Stack
        direction={'row'}
        spacing={2}
        justifyContent={'center'}
        sx={
          matches
            ? {
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                padding: 2,
                zIndex: 99,
                backgroundColor: 'white',
                borderTop: '1px solid #E0E0E0',
              }
            : undefined
        }>
        {handleBack && !isFirstStep && (
          <Button variant="outlined" color="primary" onClick={handleBack}>
            Back
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={!validStep || sendingData}>
          {!validStep
            ? 'Fill required fields to continue'
            : activeStep === 4
            ? 'Go to Payment'
            : 'Continue'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default DynamicStep;
